import classNames from 'classnames';
import { FunctionComponent, useState, useEffect, useRef } from 'react';

import { useWeblab } from '@amzn/sustainability-central-shared-react';

import { MenuItem } from 'src/components/common/menu-item/MenuItem';
import { useGetNav } from 'src/components/header/hooks/use-get-nav';
import { useGetUserRights } from 'src/components/header/hooks/use-get-user-rights';
import { ASX_CREDITS_ACCOUNT_LAUNCH_1318281 } from 'src/constants';
import { useDropdownContext } from 'src/contexts/DropdownContext';
import { NAV_ITEM_TYPE, NAV_PLACEMENT } from 'src/enums/navigation';
import { RESOURCE_BUNDLES } from 'src/enums/resource-bundles';
import { DEFAULT_THEME, useThemeConfig } from 'src/hooks/use-theme-config';
import useDeviceSize from 'src/hooks/useDeviceSize';
import windMillImage from 'src/images/windmills.png';
import { NavItem, NavItems } from 'src/interfaces/navigation';
import { isAccount, isSearch } from 'src/utils/nav-items';

import styles from './Header.module.scss';

const INITIAL_SUB_NAV_PARENT: NavItem | null = null;

const activateDenyList = [
  NAV_ITEM_TYPE.LOGO,
  NAV_ITEM_TYPE.SUST_SITE_LINK,
  NAV_ITEM_TYPE.HAMBURGER,
  NAV_ITEM_TYPE.CLOSE,
  NAV_ITEM_TYPE.LOCALE_PICKER,
  NAV_ITEM_TYPE.ACCOUNT,
];
const isActivateEligible = (item: NavItem): boolean => !activateDenyList.includes(item.itemType);

const getInitialActiveItem = (navItems: NavItems): NavItem | null => {
  let active: NavItem | null = null;
  navItems.forEach((item) => {
    if (isActivateEligible(item) && item.url === window.location.pathname) {
      active = item;
    }
  });

  return active;
};

const deviceNavDenyList = [
  NAV_ITEM_TYPE.SEARCH,
  NAV_ITEM_TYPE.LOGO,
  NAV_ITEM_TYPE.HAMBURGER,
  NAV_ITEM_TYPE.CLOSE,
  NAV_ITEM_TYPE.SUST_SITE_LINK,
];
const isDeviceNavEligible = (item: NavItem): boolean => !deviceNavDenyList.includes(item.itemType);

export const Header: FunctionComponent = () => {
  const { themeConfig, observer } = useThemeConfig();
  const isThemeWeblabEnabled = useWeblab(themeConfig.weblab ?? '') === 'T1';
  const isAccountWeblabEnabled = useWeblab(ASX_CREDITS_ACCOUNT_LAUNCH_1318281) === 'T1';
  const { isSmallDesktop } = useDeviceSize();

  const { navItems } = useGetNav();
  const { isAuthenticated } = useGetUserRights();
  const { isDropdownOpen, toggleDropdown, closeDropdown, registerDropdownRef } = useDropdownContext();

  const [subNavParent, setSubNavParent] = useState<NavItem | null>(INITIAL_SUB_NAV_PARENT);
  const isSubNavExpanded = isDropdownOpen('subnav');
  const [activeItem, setActiveItem] = useState<NavItem | null>(getInitialActiveItem(navItems));
  const headerRef = useRef<HTMLElement>(null);
  const [isDeviceMenuExpanded, setIsDeviceMenuExpanded] = useState<boolean>(false);
  const isDeviceNav = isDeviceMenuExpanded && !isSubNavExpanded;
  const isDeviceSubNav = isDeviceMenuExpanded && isSubNavExpanded;
  const sustSiteLinkItem = navItems.find((item) => item.itemType === NAV_ITEM_TYPE.SUST_SITE_LINK);
  const headerBundle = RESOURCE_BUNDLES.HEADER;

  const injectAdditionalDeviceSubNavItems = (parentItem: NavItem): NavItems => {
    const { children: items } = parentItem;
    const itemsWithSiteLink = sustSiteLinkItem ? [...items, sustSiteLinkItem] : items;

    return [parentItem, ...itemsWithSiteLink];
  };

  const [isHidden, setIsHidden] = useState(false);
  const hideHeaderYThreshold = 420;
  const showHeaderScrollUpThreshold = 300;

  // eslint-disable-next-line no-nested-ternary
  const themeValue = themeConfig.weblab
    ? isThemeWeblabEnabled
      ? themeConfig.value
      : DEFAULT_THEME.value
    : themeConfig.value;

  useEffect(() => {
    let lastScrollY = window.scrollY;
    let scrollUpDistance = 0;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (isSubNavExpanded) {
        // If subNav menus are open, header should not be hidden
        setIsHidden(false);
        scrollUpDistance = 0;
      } else if (currentScrollY > lastScrollY && currentScrollY > hideHeaderYThreshold) {
        // Scrolling down and passed a threshold, hide header
        setIsHidden(true);
        scrollUpDistance = 0;
      } else if (currentScrollY < lastScrollY) {
        // Scrolling up, show header
        if (isHidden) {
          scrollUpDistance += lastScrollY - currentScrollY;
          if (scrollUpDistance >= showHeaderScrollUpThreshold) {
            setIsHidden(false);
            scrollUpDistance = 0;
          }
        }
      }

      lastScrollY = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      observer.disconnect();
    };
  }, [isHidden, isSubNavExpanded, observer]);

  useEffect(() => {
    const handleHideHeaderContainer = () => {
      const headerContainer = document.getElementById('sc-navbar-container');
      if (headerContainer) {
        headerContainer.classList.remove('show', 'hide');
        headerContainer.classList.add(isHidden ? 'hide' : 'show');
        headerContainer.setAttribute('aria-hidden', String(isHidden));
      }
    };

    handleHideHeaderContainer();
  }, [isHidden]);

  useEffect(() => {
    const rootElement = document.documentElement;
    if (isDeviceMenuExpanded) {
      rootElement.classList.add('no-scroll');
    } else {
      rootElement.classList.remove('no-scroll');
    }
  }, [isDeviceMenuExpanded]);

  const handleDeviceMenuExpand = () => {
    setIsDeviceMenuExpanded(true);
  };

  const handleDeviceMenuCollapse = () => {
    setIsDeviceMenuExpanded(false);
    setSubNavParent(INITIAL_SUB_NAV_PARENT);
  };

  const handleMenuToggle = (navItem: NavItem) => {
    if (isDropdownOpen('subnav') && subNavParent?.id === navItem.id) {
      // Clicking the same item that's already expanded - close it
      setSubNavParent(INITIAL_SUB_NAV_PARENT);
      closeDropdown();
    } else {
      // Opening a new item or opening when closed
      setActiveItem(navItem);
      setSubNavParent(navItem);
      toggleDropdown('subnav');
    }
  };

  const handleMenuCollapse = () => {
    setSubNavParent(INITIAL_SUB_NAV_PARENT);
    closeDropdown();
  };

  const handleMouseLeaveHeader = () => {
    if (isDeviceMenuExpanded) {
      return;
    }

    setActiveItem(getInitialActiveItem(navItems));

    if (isSubNavExpanded) {
      handleMenuCollapse();
    }
  };

  const handleMouseEnterSubNav = () => {
    setActiveItem(subNavParent);
  };

  const handleItemActivate = (navItem: NavItem) => {
    setActiveItem(navItem);
  };

  // On device surfaces, reposition the search icon to be second last
  const repositionSearchItem = (items: NavItems) => {
    if (isSmallDesktop) return items;
    const searchItem = items.find((item) => isSearch(item));
    if (searchItem) {
      const searchItemIndex = items.indexOf(searchItem);
      items.splice(searchItemIndex, 1);
      items.splice(-2, 0, searchItem);
    }
    return items;
  };

  // Register header with dropdown context for outside click detection
  useEffect(() => {
    registerDropdownRef('subnav', headerRef.current);
    return () => registerDropdownRef('subnav', null);
  }, [registerDropdownRef]);

  // Close subnav parent when dropdown context closes subnav
  useEffect(() => {
    if (!isDropdownOpen('subnav') && subNavParent) {
      setSubNavParent(INITIAL_SUB_NAV_PARENT);
    }
  }, [isDropdownOpen, subNavParent]);

  // Normally we'd fetch strings from Panther and wait for isBundleLoading to render a nav, but the translations for
  // the nav items come from NRS so we can use those and get the nav on the page sooner and decrease rendering lag
  return (
    <header
      ref={headerRef}
      className={classNames(styles.header, styles[themeValue], {
        [styles.expanded]: isSubNavExpanded || isDeviceMenuExpanded,
      })}
      onMouseLeave={handleMouseLeaveHeader}
    >
      <nav aria-label="primary" className={styles.navigation}>
        {/* Desktop Nav menu and device top nav */}
        <ul className={styles.listContainer}>
          {repositionSearchItem(navItems)
            .filter((item) => !isAccount(item) || isAccountWeblabEnabled)
            .map((item) => (
              <MenuItem
                isAlignedRight={isSearch(item)}
                isAuthenticated={isAuthenticated}
                isDeviceMenuExpanded={isDeviceMenuExpanded}
                isItemActive={item.id === activeItem?.id}
                isItemExpanded={item.id === subNavParent?.id}
                isNavExpanded={isSubNavExpanded}
                key={item.id}
                navItem={item}
                onDeviceMenuCollapse={handleDeviceMenuCollapse}
                onDeviceMenuExpand={handleDeviceMenuExpand}
                onItemActivate={isActivateEligible(item) ? handleItemActivate : undefined}
                onMenuExpand={handleMenuToggle}
                onMenuCollapse={handleMenuCollapse}
                placement={NAV_PLACEMENT.HEADER}
                theme={themeValue}
                resourceBundle={headerBundle}
              />
            ))}
        </ul>

        {/* Desktop SubNav menu */}
        {isSubNavExpanded && !isDeviceMenuExpanded && subNavParent && (
          <div
            data-testid="subnav-container"
            className={classNames(styles.subNavContainer, styles[themeValue])}
            onMouseEnter={handleMouseEnterSubNav}
          >
            <ul className={styles.subNavListContainer}>
              {subNavParent.children.map((item) => (
                <MenuItem
                  isDeviceMenuExpanded={isDeviceMenuExpanded}
                  isNavExpanded={isSubNavExpanded}
                  key={item.id}
                  navItem={item}
                  placement={NAV_PLACEMENT.SUB_NAV}
                  theme={themeValue}
                  resourceBundle={headerBundle}
                />
              ))}
            </ul>
            {/* TODO: Implement dynamic image when hover over sub nav item: https://app.asana.com/0/1206972973144706/1207403964742386 */}
            <img className={styles.subNavImage} src={windMillImage} alt="placeholder" />
          </div>
        )}

        {/* Device Nav and SubNav menu */}
        {isDeviceMenuExpanded && (
          <ul
            className={classNames(styles.deviceListContainer, styles[themeValue], {
              [styles.deviceNavListContainer]: isDeviceNav,
              [styles.deviceSubNavListContainer]: isDeviceSubNav,
              [styles.isAccountWeblabEnabled]: isAccountWeblabEnabled,
            })}
          >
            {/* Device Nav menu items */}
            {isDeviceNav &&
              navItems
                .filter(isDeviceNavEligible)
                .filter((item) => !isAccount(item) || isAccountWeblabEnabled)
                .map((item) => (
                  <MenuItem
                    isAuthenticated={isAuthenticated}
                    isDeviceMenuExpanded={isDeviceMenuExpanded}
                    key={item.id}
                    navItem={item}
                    onMenuExpand={handleMenuToggle}
                    placement={NAV_PLACEMENT.DEVICE_NAV}
                    theme={themeValue}
                    resourceBundle={headerBundle}
                  />
                ))}

            {/* Device SubNav menu items */}
            {isDeviceSubNav &&
              subNavParent &&
              injectAdditionalDeviceSubNavItems(subNavParent).map((item) => (
                <MenuItem
                  isDeviceMenuExpanded={isDeviceMenuExpanded}
                  isItemExpanded={item.id === subNavParent?.id}
                  key={item.id}
                  navItem={item}
                  onMenuCollapse={handleMenuCollapse}
                  placement={NAV_PLACEMENT.DEVICE_SUB_NAV}
                  theme={themeValue}
                  resourceBundle={headerBundle}
                />
              ))}
          </ul>
        )}
      </nav>
    </header>
  );
};
